
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { Box, Paper, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Feature } from 'ol';
import { LineString, Point } from 'ol/geom';
import { Style, Stroke, Circle, Fill, Text } from 'ol/style';
import { fromLonLat } from 'ol/proj';
import polyline from '@mapbox/polyline';

export default function SimpleOpenLayersMap({ center = [74.2515, 16.7041], zoom = 10, journeys = [], selectedJourneys = {}, optimizedData = null }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const vectorLayerRef = useRef(null);

  // Define an array of colors for different journeys
  const journeyColors = [
    '#FF0000', // Red
    '#00FF00', // Green
    '#0000FF', // Blue
    '#FFA500', // Orange
    '#800080', // Purple
    '#008000', // Dark Green
  ];

  useEffect(() => {
    if (!mapRef.current) return;

    // Initialize map
    const tileLayer = new TileLayer({
      source: new OSM(),
    });

    const map = new Map({
      target: mapRef.current,
      layers: [tileLayer],
      view: new View({
        center: fromLonLat(center),
        zoom,
      }),
      controls: [],
    });

    mapInstanceRef.current = map;

    // Initialize vector layer for routes and stops
    vectorLayerRef.current = new VectorLayer({
      source: new VectorSource(),
      style: (feature) => {
        const geometryType = feature.getGeometry().getType();
        if (geometryType === 'LineString') {
          const colorIndex = feature.get('colorIndex'); // Get the color index from the feature
          return new Style({
            stroke: new Stroke({
              color: journeyColors[colorIndex % journeyColors.length], // Use the assigned color
              width: 3,
            }),
          });
        } else if (geometryType === 'Point') {
          const stopIndex = feature.get('stopIndex');
          return new Style({
            image: new Circle({
              radius: 10,
              fill: new Fill({ color: '#000000' }), // Black for stops
              stroke: new Stroke({ color: '#FFFFFF', width: 2 }),
            }),
            text: new Text({
              text: stopIndex !== undefined ? stopIndex.toString() : '', // Number inside marker
              font: '12px sans-serif',
              fill: new Fill({ color: '#FFFFFF' }), // White text for contrast
              stroke: new Stroke({ color: '#000000', width: 2 }),
              offsetY: 0, // Center the number inside the marker
            }),
          });
        }
        return null;
      },
    });
    map.addLayer(vectorLayerRef.current);

    const handleResize = () => map.updateSize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      map.setTarget(null);
    };
  }, [center, zoom]);

// SimpleOpenLayersMap.js ke useEffect mein ye check aur update karein:

useEffect(() => {
  if (!mapInstanceRef.current || !vectorLayerRef.current) return;

  const source = vectorLayerRef.current.getSource();
  source.clear(); 

  // Filter selected journeys
  const selectedJourneyIds = Object.keys(selectedJourneys).filter((id) => selectedJourneys[id]);
  const selectedJourneysData = journeys.filter((journey) => selectedJourneyIds.includes(journey.journeyId));

  selectedJourneysData.forEach((journey, index) => {
    try {
      // 1. Check if encodedPath exists
      if (journey.encodedPath) {
        const decodedCoords = polyline.decode(journey.encodedPath);
        
        // IMPORTANT: OpenLayers hamesha [Lon, Lat] leta hai
        // polyline.decode [Lat, Lon] deta hai, toh use swap karna padta hai
        const routeCoords = decodedCoords.map(([lat, lon]) => fromLonLat([lon, lat]));

        const routeFeature = new Feature({
          geometry: new LineString(routeCoords),
          journeyId: journey.journeyId,
          colorIndex: index, 
        });
        source.addFeature(routeFeature);
      }
    } catch (error) {
      console.error("Error drawing route line:", error);
    }

    // Markers Plotting Logic
    journey.stopPoints.forEach((stop, stopIndex) => {
      if (stop.location && stop.location.latitude) {
        const stopFeature = new Feature({
          geometry: new Point(fromLonLat([stop.location.longitude, stop.location.latitude])),
          name: stop.locationName,
          stopIndex: stopIndex + 1,
        });
        source.addFeature(stopFeature);
      }
    });
  });

  // Zoom to fit the route
  if (source.getFeatures().length > 0) {
    const extent = source.getExtent();
    mapInstanceRef.current.getView().fit(extent, {
      padding: [50, 50, 50, 50],
      duration: 1000
    });
  }
}, [journeys, selectedJourneys]);

  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.getView().setZoom(mapInstanceRef.current.getView().getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.getView().setZoom(mapInstanceRef.current.getView().getZoom() - 1);
    }
  };

  return (
    <Paper elevation={3} sx={{ position: "relative", width: "100%", height: 500, borderRadius: 2, overflow: "hidden" }}>
      <Box ref={mapRef} sx={{ width: "100%", height: "100%" }} />
      <Box sx={{ position: "absolute", top: 16, left: 16, display: "flex", flexDirection: "column", gap: 1 }}>
        <IconButton size="small" color="primary" onClick={handleZoomIn} sx={{ bgcolor: "white" }}>
          <AddIcon />
        </IconButton>
        <IconButton size="small" color="primary" onClick={handleZoomOut} sx={{ bgcolor: "white" }}>
          <RemoveIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}

SimpleOpenLayersMap.propTypes = {
  center: PropTypes.arrayOf(PropTypes.number),
  zoom: PropTypes.number,
  journeys: PropTypes.arrayOf(
    PropTypes.shape({
      journeyId: PropTypes.string,
      journeyName: PropTypes.string,
      type: PropTypes.string,
      interval: PropTypes.shape({
        startTime: PropTypes.string,
        endTime: PropTypes.string,
      }),
      encodedPath: PropTypes.string,
      stopPoints: PropTypes.arrayOf(
        PropTypes.shape({
          locationName: PropTypes.string,
          location: PropTypes.shape({
            latitude: PropTypes.number,
            longitude: PropTypes.number,
          }),
        })
      ),
    })
  ),
  selectedJourneys: PropTypes.object,
};

SimpleOpenLayersMap.defaultProps = {
  center: [74.2515, 16.7041], // Approximate center for Kolhapur, Maharashtra
  zoom: 10,
  journeys: [],
  selectedJourneys: {},
};